
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  TourProvider as ReactTourProvider,
  useTour as useReactTour
} from '@reactour/tour';
import TourModal from './tour-modal';

type TourStep = {
  selector: string;
  content: string;
  position?: 'top' | 'right' | 'bottom' | 'left' | 'center';
  page?: string;
  navigationDelay?: number;
  disableBeacon?: boolean;
  action?: (elem: Element | null) => void;
  actionBefore?: (elem: Element | null) => void;
};

type TourContextType = {
  addSteps: (steps: TourStep[]) => void;
  startTour: (options?: { showModal?: boolean }) => void;
  stopTour: () => void;
  clearSteps: () => void;
  isRunning: boolean;
  currentStepIndex: number;
};

const TourContext = createContext<TourContextType>({
  addSteps: () => {},
  startTour: () => {},
  stopTour: () => {},
  clearSteps: () => {},
  isRunning: false,
  currentStepIndex: 0
});

export const useTourContext = () => useContext(TourContext);

const TourProviderInner = ({ children }: { children: React.ReactNode }) => {
  const [allSteps, setAllSteps] = useState<TourStep[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [currentGlobalStep, setCurrentGlobalStep] = useState(0);
  const [showStartModal, setShowStartModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<{
    stepIndex: number;
    page: string;
  } | null>(null);

  const navigate = useNavigate();
  const pathname = useLocation();
  const { setIsOpen: setTourOpen, setCurrentStep, setSteps } = useReactTour();
  // const { hasSeenTour, markTourAsSeen } = useTourControl();
  // const currentPageSteps = allSteps.filter(
  //   (step) => !step.page || step.page === pathname || step.page === '*'
  // );

  const addSteps = useCallback((newSteps: TourStep[]) => {
    setAllSteps((prev) => [...prev, ...newSteps]);
  }, []);

  const startTour = useCallback(
    (options?: { showModal?: boolean }) => {
      if (options?.showModal) {
        setShowStartModal(true);
      } else {
        setCurrentGlobalStep(0);
        setIsOpen(true);
        setTourOpen(true);
      }
    },
    [setTourOpen]
  );

  const stopTour = useCallback(() => {
    setIsOpen(false);
    setTourOpen(false);
    setCurrentGlobalStep(0);
    setShowStartModal(false);
    setPendingNavigation(null);
  }, [setTourOpen]);

  const clearSteps = useCallback(() => {
    setAllSteps([]);
    stopTour();
  }, [stopTour]);

  // Handle pending navigation completion
  useEffect(() => {
    if (pendingNavigation && pathname.pathname === pendingNavigation.page) {
      const timer = setTimeout(() => {
        setCurrentGlobalStep(pendingNavigation.stepIndex);
        setIsOpen(true);
        setTourOpen(true);
        setPendingNavigation(null);
      }, 500); // Additional delay for page stabilization

      return () => clearTimeout(timer);
    }
  }, [pathname, pendingNavigation, setTourOpen]);

  // Update react-tour steps when current page changes or tour starts
  useEffect(() => {
    if (!isOpen) return;

    const currentStepData = allSteps[currentGlobalStep];
    const isOnCorrectPage =
      !currentStepData?.page ||
      currentStepData.page === '*' ||
      currentStepData.page === pathname.pathname;

    if (!isOnCorrectPage) {
      setIsOpen(false);
      setPendingNavigation({
        stepIndex: currentGlobalStep,
        page: currentStepData.page!
      });
      navigate(currentStepData.page!);
      return;
    }

    const pageSteps = allSteps.filter(
      (step) => !step.page || step.page === pathname.pathname || step.page === '*'
    );

    const currentPageIndex = pageSteps.findIndex(
      (step) => step === allSteps[currentGlobalStep]
    );

    if (currentPageIndex === -1) {
      stopTour();
      return;
    }

    const reactTourSteps = pageSteps.map((step) => ({
      selector: step.selector,
      content: step.content,
      position: step.position || 'bottom',
      action: step.action,
      actionBefore: step.actionBefore
    }));

    setSteps?.(reactTourSteps);
    setCurrentStep(currentPageIndex);
  }, [
    isOpen,
    currentGlobalStep,
    pathname,
    allSteps,
    setSteps,
    setCurrentStep,
    navigate,
    stopTour
  ]);

  const handleModalConfirm = () => {
    setShowStartModal(false);
    startTour();
  };

  return (
    <TourContext.Provider
      value={{
        addSteps,
        startTour,
        stopTour,
        clearSteps,
        isRunning: isOpen,
        currentStepIndex: currentGlobalStep
      }}
    >
      <div data-tour-context='true'>
        {showStartModal && (
          <TourModal onConfirm={handleModalConfirm} onCancel={stopTour} />
        )}
        {children}
      </div>
    </TourContext.Provider>
  );
};

export const TourProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <ReactTourProvider
      steps={[]}
      currentStep={0}
      onClickMask={({ setIsOpen }) => setIsOpen(false)}
      onClickClose={({ setIsOpen }) => setIsOpen(false)}
      className='reactour__popover'
      styles={{
        popover: (base) => ({
          ...base,
          '--reactour-accent': '#2563eb',
          borderRadius: '8px',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
        }),
        maskArea: (base) => ({
          ...base,
          rx: 4
        }),
        badge: (base) => ({
          ...base,
          left: 'auto',
          right: '-0.8125em'
        })
      }}
      showNavigation
      showPrevNextButtons
      showCloseButton
      showBadge
      scrollSmooth
      disableInteraction
    >
      <TourProviderInner>{children}</TourProviderInner>
    </ReactTourProvider>
  );
};
